/**
 * Family Residence Form Component
 * Reusable form for family-specific fields
 */

import { DatePicker } from '@components/ui/DatePicker'
import { FormInput, FormSelect, FormField } from '@components/ui/FormField'
import { formatDateForAPI, formatDateForDisplay } from '@/features/applicant/utils/addressHelpers'
import { convertToDateInputFormat } from '@/lib/utils/dateValidation'
import { filterPhoneInput, formatCurrencyInput, getNumericCurrencyValue } from '@/lib/utils/formatters'
import { APPLICANT_CONTENT } from '@/features/applicant/constants/applicantContent'
import type { FamilyFormErrors } from './types'

interface FamilyFormProps {
  // Form values
  contactName: string
  contactRelationship: string
  contactEmail: string
  contactMobile: string
  contactWebsite: string
  moveInDate: string
  plannedMoveOutDate: string
  rentAmount: string
  monthsPaid?: string
  stillHere: boolean

  // Form handlers
  onContactNameChange: (value: string) => void
  onContactRelationshipChange: (value: string) => void
  onContactEmailChange: (value: string) => void
  onContactMobileChange: (value: string) => void
  onContactWebsiteChange: (value: string) => void
  onMoveInDateChange: (value: string) => void
  onPlannedMoveOutDateChange: (value: string) => void
  onRentAmountChange: (value: string) => void
  onMonthsPaidChange?: (value: string) => void
  onStillHereChange: (value: boolean) => void

  // Validation errors
  errors?: FamilyFormErrors
  isReadOnly?: boolean
  isPreviousAddress?: boolean
}

export function FamilyForm({
  contactName,
  contactRelationship,
  contactEmail,
  contactMobile,
  moveInDate,
  plannedMoveOutDate,
  rentAmount,
  monthsPaid,
  stillHere,
  onContactNameChange,
  onContactRelationshipChange,
  onContactEmailChange,
  onContactMobileChange,
  onMoveInDateChange,
  onPlannedMoveOutDateChange,
  onRentAmountChange,
  onMonthsPaidChange,
  onStillHereChange,
  errors = {},
  isReadOnly = false,
  isPreviousAddress = false,
}: FamilyFormProps) {
  return (
    <div className="border-l-4 border-blue-500 bg-gradient-to-r from-blue-50 to-indigo-50 p-6 mb-6 rounded-r-lg shadow-sm">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 [&>div]:min-w-0">
        <FormField
          label={APPLICANT_CONTENT.application.sections.currentAddress.familyFields.contactName}
          required
        >
          <FormInput
            type="text"
            value={contactName}
            onChange={(e) => onContactNameChange(e.target.value)}
            placeholder={APPLICANT_CONTENT.application.sections.currentAddress.familyFields.contactName}
            error={errors.contactName}
            disabled={isReadOnly}
          />
        </FormField>
        <FormField
          label={APPLICANT_CONTENT.application.sections.currentAddress.familyFields.contactRelationship}
          required
        >
          <FormSelect
            value={contactRelationship}
            onChange={(e) => onContactRelationshipChange(e.target.value)}
            error={errors.contactRelationship}
            disabled={isReadOnly}
          >
            <option value="">Select</option>
            <option value="parent_child">Parent/child</option>
            <option value="relative">Relative</option>
            <option value="friend">Friend</option>
          </FormSelect>
        </FormField>
        <FormField
          label={APPLICANT_CONTENT.application.sections.currentAddress.familyFields.contactEmail}
          required
        >
          <FormInput
            type="email"
            value={contactEmail}
            onChange={(e) => onContactEmailChange(e.target.value)}
            placeholder={APPLICANT_CONTENT.application.sections.currentAddress.familyFields.contactEmail}
            error={errors.contactEmail}
            disabled={isReadOnly}
          />
        </FormField>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 [&>div]:min-w-0">
        <FormField
          label={APPLICANT_CONTENT.application.sections.currentAddress.familyFields.contactMobile}
          required
        >
          <FormInput
            type="tel"
            value={contactMobile}
            onChange={(e) => onContactMobileChange(filterPhoneInput(e.target.value))}
            placeholder={APPLICANT_CONTENT.application.sections.currentAddress.familyFields.contactMobile}
            error={errors.contactMobile}
            disabled={isReadOnly}
          />
        </FormField>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {APPLICANT_CONTENT.application.sections.currentAddress.rentFields.moveInDate}*
          </label>
          <DatePicker
            value={moveInDate ? convertToDateInputFormat(moveInDate) : ''}
            onChange={(value) => {
              const displayValue = formatDateForDisplay(value)
              onMoveInDateChange(displayValue)
            }}
            placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.dateOfBirth}
            minDate={new Date(1900, 0, 1)}
            maxDate={
              plannedMoveOutDate
                ? new Date(Math.min(new Date().getTime(), new Date(formatDateForAPI(plannedMoveOutDate)).getTime()))
                : new Date()
            }
            showHelperText={false}
            error={errors.moveInDate}
            disabled={isReadOnly}
          />
        </div>
        {!isPreviousAddress && (
          <div>
            <div className="flex items-center gap-4 mb-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={stillHere}
                  onChange={(e) => {
                    if (!isReadOnly) {
                      onStillHereChange(e.target.checked)
                      if (e.target.checked) {
                        onPlannedMoveOutDateChange('')
                      }
                    }
                  }}
                  disabled={isReadOnly}
                  className="mr-2 w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                />
                <span className="text-sm text-gray-700">{APPLICANT_CONTENT.application.sections.currentAddress.rentFields.stillHere}</span>
              </label>
              <span className="text-sm text-gray-700">{APPLICANT_CONTENT.application.sections.currentAddress.ownFields.plannedMoveOutDate}</span>
            </div>
            <DatePicker
              value={plannedMoveOutDate ? convertToDateInputFormat(plannedMoveOutDate) : ''}
              onChange={(value) => {
                const displayValue = formatDateForDisplay(value)
                onPlannedMoveOutDateChange(displayValue)
              }}
              placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.dateOfBirth}
              minDate={(() => {
                const today = new Date()
                today.setHours(0, 0, 0, 0)
                if (moveInDate) {
                  const moveIn = new Date(formatDateForAPI(moveInDate))
                  return moveIn > today ? moveIn : today
                }
                return today
              })()}
              allowFutureDates={true}
              disabled={isReadOnly || stillHere}
              showHelperText={false}
              error={errors.plannedMoveOutDate}
            />
          </div>
        )}
        <FormField
          label={APPLICANT_CONTENT.application.sections.currentAddress.familyFields.rentAmount}
          required
        >
          <FormInput
            type="text"
            value={rentAmount ? formatCurrencyInput(rentAmount) : ''}
            onChange={(e) => {
              // Extract numeric value from formatted input
              const numericValue = getNumericCurrencyValue(e.target.value)
              // Store only the numeric value
              onRentAmountChange(numericValue)
            }}
            onBlur={() => {
              // On blur, strip any stray formatting so state stays numeric
              if (rentAmount) {
                const numeric = getNumericCurrencyValue(rentAmount)
                if (numeric !== rentAmount) {
                  onRentAmountChange(numeric)
                }
              }
            }}
            placeholder="$0.00"
            disabled={isReadOnly}
          />
        </FormField>

        {onMonthsPaidChange && (
          <FormField
            label={APPLICANT_CONTENT.application.sections.currentAddress.rentFields.monthsPaidRent}
            required
          >
            <FormInput
              type="text"
              value={monthsPaid || '0'}
              onChange={() => { }}
              placeholder="Paid Rent"
              error={errors.monthsPaidRent}
              disabled={true}
            />
          </FormField>
        )}
      </div>
    </div>
  )
}

